import { Collapse, Form } from "antd";
import { RiskLevelType } from "components/date-block/models";
import linkedCell from "components/links/linkedCell";
import LinkIcon from "components/svg-icons/link-icon";
import AsterixIcon from "components/svg-icons/asterix-icon";
import { getSubmittalLinkedBidPackages } from "entity-app/shared-components/entity-dateblock/utils/get-submittal-linked-bid-packages";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { DateUtils } from "utils/dateutils";

function getBidPackageStartDate(link: {
  workflow_instance_milestones?: Array<{
    sequence_no?: number;
    actual_date?: string | null;
    projected_date?: string | null;
    planned_date?: string | null;
  }>;
}): string | null {
  const milestones = [...(link.workflow_instance_milestones ?? [])].sort(
    (a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0)
  );
  const first = milestones[0];
  if (!first) return null;
  const raw =
    first.actual_date ?? first.projected_date ?? first.planned_date ?? null;
  return raw ? DateUtils.format(raw) : null;
}

function bidPackagePanelHeader(link: {
  number?: string;
  auto_inc_value?: string | number;
  title?: string;
  governing_link?: boolean;
  workflow_instance_milestones?: Array<{
    sequence_no?: number;
    actual_date?: string | null;
    projected_date?: string | null;
    planned_date?: string | null;
  }>;
}) {
  const displayNumber = link.number ?? link.auto_inc_value;
  const startDate = getBidPackageStartDate(link);
  const title = displayNumber
    ? `${displayNumber}-${link.title}`
    : (link.title ?? "");

  return (
    <div className="flex w-full min-w-0 items-center" title={title}>
      <span className="flex shrink-0 items-center mr-2">
        <LinkIcon />
      </span>
      {displayNumber ? (
        <i className="shrink-0 opacity-60 whitespace-nowrap">
          {displayNumber}
          {!link.governing_link && <span className="mx-1">-</span>}
        </i>
      ) : null}
      {link.governing_link ? (
        <span className="flex shrink-0 items-center mx-1 mb-1">
          <AsterixIcon />
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate mr-1">{link.title}</span>
      {startDate ? (
        <i className="shrink-0 whitespace-nowrap px-1 opacity-60 italic">
          {`Start: ${startDate}`}
        </i>
      ) : null}
    </div>
  );
}

export default function FeatureBidPackagesLinks(props: {
  featureInstanceData: any;
}) {
  const { featureInstanceData } = props;
  const { projectId } = useParams() as { projectId: string };
  const { Panel } = Collapse;
  const cssForValue = "text-xs font-normal text-black text-opacity-80";

  const bidPackageLinks = useMemo(
    () => getSubmittalLinkedBidPackages(featureInstanceData),
    [featureInstanceData]
  );

  return (
    <Form layout="vertical" data-testid="flbp-form">
      <Form.Item
        label={
          <span data-testid="flbp-title-count">
            Bid Packages
            {bidPackageLinks.length ? ` (${bidPackageLinks.length})` : ""}
          </span>
        }
        data-testid="flbp-form-item"
      >
        {bidPackageLinks.length > 0 ? (
          <Collapse
            accordion
            expandIconPlacement="end"
            className="custom-collapse bid-package-links-collapse"
            data-testid="flbp-collapse"
          >
            {bidPackageLinks.map((link) => {
              const status =
                link.feature_workflow_instance?.milestone_state ?? "";
              const riskLevel = link.feature_workflow_instance?.risk_level;

              return (
                <Panel
                  header={bidPackagePanelHeader(link)}
                  key={link.id}
                  className={
                    riskLevel === RiskLevelType.High
                      ? "custom-panel risk-panel truncate"
                      : "custom-panel truncate"
                  }
                  data-testid="flbp-panel"
                >
                  <div
                    className="flex-col space-y-1"
                    data-testid="flbp-panel-content"
                  >
                    <div
                      className="grid grid-cols-2"
                      data-testid="flbp-details-grid"
                    >
                      <div
                        className="grid grid-rows-2 gap-1"
                        data-testid="flbp-left-column"
                      >
                        {linkedCell("Status:", status, "", cssForValue)}
                        {linkedCell(
                          "Risk:",
                          riskLevel ?? "",
                          "",
                          riskLevel === RiskLevelType.High
                            ? "date-block-risk-high text-xs font-semibold"
                            : cssForValue
                        )}
                      </div>
                    </div>
                    <div
                      className="flex justify-end items-center"
                      data-testid="flbp-view-link"
                    >
                      <Link
                        className="border-solid border-gray-300 hover:text-black hover:border-black border-[1px] rounded px-2 py-1 text-xs text-black"
                        to={{
                          pathname: `/project/${projectId}/bid-packages/${link.id}`
                        }}
                        data-testid="flbp-link-view-bid-package"
                      >
                        View Bid Package
                      </Link>
                    </div>
                  </div>
                </Panel>
              );
            })}
          </Collapse>
        ) : (
          <span
            className="text-xs text-neutral-400"
            data-testid="flbp-no-links"
          >
            No links added
          </span>
        )}
      </Form.Item>
    </Form>
  );
}
